import React, { useEffect, useState } from "react";
import GalleryContainer from "../components/GalleryContainer";

import { Image } from "../types/Image";
import { supabase } from "../lib/supabase";

const ITEMS_PER_PAGE = 20;

const Gallery: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [modalImages, setModalImages] = useState<Image[]>([]);
  const [showText, setShowText] = useState(false);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentImages = images.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const totalPages = Math.ceil(images.length / ITEMS_PER_PAGE);


  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const fetchGallery = async () => {
      const { data, error } = await supabase.from('gallery').select('*');
      if (error) {
        console.error("Görseller alınamadı:", error);
      } else {
        setImages(data as Image[]);
      }
      setLoading(false);
    };
    fetchGallery();
  }, [currentPage]);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [modalOpen]);

  const openModal = (index: number, images: Image[]) => {
    setModalIndex(index);
    setModalImages(images);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen">
      {/* Başlık */}
      <section className="text-center py-10 px-6 bg-[#002744] text-white">
        <h1 className="text-4xl md:text-6xl font-bold mb-4">Galeri</h1>
        <p className="text-lg max-w-2xl mx-auto opacity-80 md:whitespace-nowrap">
          Özenle üretilmiş teknelerimizden seçilmiş kareler
        </p>
      </section>

      {loading ? (
        <div className="flex justify-center items-center min-h-[300px]">
          <div className="w-12 h-12 border-4 border-[#009fe3] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : currentImages.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center min-h-[300px] py-20">
          <div className="text-6xl mb-4">📷</div>
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            Bu sayfada gösterilecek görsel bulunamadı.
          </h2>
          <p className="text-gray-500 max-w-md">
            Henüz eklenmiş fotoğraf yok gibi görünüyor. Yakında yeni karelerle
            galeri dolacak!
          </p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 text-center mb-12 mt-20">
          <GalleryContainer
            images={currentImages}
            onImageClick={(index) => openModal(index, currentImages)}
          />
        </div>
      )}

      {/* Sayfalama */}
      {images.length > ITEMS_PER_PAGE ? (
        <div>
          {" "}
          <div className="flex justify-center items-center gap-2 text-black mt-8 min-h-[80px]">
            <button
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              ◀ Önceki
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${
                  currentPage === i + 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Sonraki ▶
            </button>
          </div>
        </div>
      ) : (
        <div> </div>
      )}

      {/* Modal */}
      {modalOpen && modalIndex !== null && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
          onClick={() => setModalOpen(false)}
        >
          {/* Blur arka plan */}
          <div
            className="absolute top-0 left-0 w-full h-full bg-center bg-cover blur-xl scale-[1.2] brightness-50 z-0"
            style={{
              backgroundImage: `url(${modalImages[modalIndex].url})`,
            }}
          ></div>

          {/* Ana içerik */}
          <div
            className="relative w-[100dvw] h-[100dvh] z-10 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={modalImages[modalIndex].url}
              alt="selected"
              className="w-screen h-screen object-contain rounded-xl"
            />

            {showText && (
              <h2 className="absolute top-10 left-0 text-white text-xl md:text-2xl font-bold font-montserrat bg-black/70 px-4 py-2 rounded-xl">
                {modalImages[modalIndex].title}
              </h2>
            )}

            {showText && (
              <p className="absolute bottom-4 left-4 text-white text-sm md:text-lg bg-black/70 px-4 py-2 rounded-xl max-w-[95%]">
                {modalImages[modalIndex].description}
              </p>
            )}

            <button
              className="absolute top-2 right-2 text-white text-xl md:text-3xl font-bold bg-black/70 px-4 py-1 rounded-xl"
              onClick={() => setModalOpen(false)}
            >
              &times;
            </button>

            <div className="absolute top-2 left-2 flex items-center gap-2 bg-black/70 px-3 py-1 rounded-md text-white text-xs md:text-sm z-40">
              <input
                type="checkbox"
                checked={showText}
                onChange={() => setShowText(!showText)}
                id="toggleText"
                className="accent-blue-500 w-4 h-4"
              />
              <label htmlFor="toggleText">Başlık & Açıklama</label>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setModalIndex(
                  (prev) =>
                    (prev! - 1 + modalImages.length) % modalImages.length
                );
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full px-3 py-2"
            >
              ◀
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setModalIndex((prev) => (prev! + 1) % modalImages.length);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full px-3 py-2"
            >
              ▶
            </button>

            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
              {modalImages.map((_, i) => (
                <span
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalIndex(i);
                  }}
                  className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-300 ${
                    i === modalIndex
                      ? "bg-white scale-125 shadow-md"
                      : "bg-gray-400/70 hover:bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
